import DateFnsUtils from '@date-io/date-fns';
import {
  Box,
  Divider,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';
import { TranslatedTextV2 } from 'components/atoms/TranslatedText';
import useDebounce from 'hooks/useDebounce';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelectLanguageID } from 'redux/reducers/gl_translations_reducer/selectors';
import { ITabContentProps, PropertyFilterType } from '../configuration';
import ProductFilterActions from '../store/actions';
import * as ProductFilterSelectors from '../store/selectors';
import { SpecificationAttributeType } from 'constants/specificationAttribute';
import _, { isArray } from 'lodash';
import { isObjectNullish } from 'utils/objects';
import { formatDateToSQL } from 'utils/dates';
import QuantityModifier from 'components/app_common/QuantityModifier';
import { IOSSwitch } from 'components/molecules/FormControls/switch';
import useStyles from '../styles';
import { AppCheckbox } from 'components/atoms/StyledFormControls';

const TabOther = (props: ITabContentProps) => {
  const dispatch = useDispatch();
  const lan = useSelectLanguageID();
  const theme = useTheme();
  React.useEffect(() => {
    dispatch(ProductFilterActions.setCustomFilteredSpecs(lan));
  }, [dispatch]);
  const customAttributes = ProductFilterSelectors.useGetCustomFilteredSpecs();

  return (
    <Box>
      <Typography
        style={{
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '20px',
          marginBottom: '8px',
        }}
      >
        <TranslatedTextV2 translationKey={''} defaultText={'Other'} />
      </Typography>
      <Typography
        style={{
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '16px',
          color: theme.palette.text.secondary,
          marginBottom: '18px',
        }}
      >
        <TranslatedTextV2
          translationKey={''}
          defaultText={'Select one or more'}
        />
      </Typography>
      {customAttributes && customAttributes.length > 0 && (
        <Box display={'flex'} flexDirection={'column'}>
          {customAttributes
            ?.filter((item) => item.filtering)
            .map((value) => {
              return (
                <InputField
                  key={value.id}
                  type={value.type}
                  name={value.systemname}
                  label={value.name}
                  selections={props.selections}
                  options={value.options?.map((option) => ({
                    id: option.id,
                    label: option.name,
                  }))}
                />
              );
            })}
        </Box>
      )}
    </Box>
  );
};

interface IInputFieldProps {
  type: SpecificationAttributeType;
  name: string;
  label: string;
  selections: ITabContentProps['selections'];
  options?: { id: number; label: string }[] | undefined;
}

const InputField = ({
  type,
  name,
  selections,
  label,
  options = [],
}: IInputFieldProps) => {
  const dispatch = useDispatch();
  const data = selections[PropertyFilterType.Other].data;
  const classes = useStyles();
  const onChange = (value: string, name: string) => {
    if (name) {
      let finalValue: any = value;
      if (!value) finalValue = undefined;
      if (type === SpecificationAttributeType.Tag) {
        const dataTypeTag = data[`${name}`]?.split(',') || [];
        if (dataTypeTag?.indexOf(value) >= 0) {
          finalValue = dataTypeTag
            ?.filter((item: string) => item !== value)
            .join(',');
        } else {
          finalValue = dataTypeTag.concat(value).join(',');
        }
      }
      dispatch(
        ProductFilterActions.setFiltersSelectionAction({
          ...selections,
          [PropertyFilterType.Other]: {
            ...selections[PropertyFilterType.Other],
            isChecked: !isObjectNullish({
              ...selections[PropertyFilterType.Other].data,
              [name]: finalValue,
            }),
            data: {
              ...selections[PropertyFilterType.Other].data,
              [name]: finalValue,
            },
          },
        })
      );
    }
  };
  return (
    <Box marginBottom={'6px'}>
      {type === SpecificationAttributeType.Switch ? (
        <section>
          <label>{label}</label>
          <IOSSwitch
            title={label}
            name={name}
            value={_.get(data, name, false)}
            onChange={(event) =>
              onChange(event.target.checked.toString(), name)
            }
          />
        </section>
      ) : type === SpecificationAttributeType.Date ? (
        <DateInputField
          label={label}
          name={name}
          value={
            _.get(data, name, null)
              ? new Date(`${_.get(data, name, null)}`)
              : null
          }
          onChange={onChange}
        />
      ) : type === SpecificationAttributeType.Tag ? (
        <>
          <Typography
            style={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '20px',
              marginBottom: '4px',
            }}
          >
            {label}
          </Typography>
          <Box>
            {(options && options.length > 0 ? options : []).map(
              (option, index) => {
                return (
                  <React.Fragment key={option.id}>
                    <Box
                      display={'flex'}
                      flexDirection={'row'}
                      alignItems={'center'}
                      style={{
                        padding: '.25rem',
                        paddingLeft: 0,
                        cursor: 'pointer',
                      }}
                      onClick={() => onChange(`${option.id}`, name)}
                    >
                      <AppCheckbox
                        size={'medium'}
                        checked={
                          (data[`${name}`] || '')
                            ?.split(',')
                            ?.indexOf(`${option.id}`) !== -1
                        }
                      />
                      <Typography style={{ marginLeft: '12px' }}>
                        {option.label}
                      </Typography>
                    </Box>
                    {index + 1 < options.length ? <Divider /> : null}
                  </React.Fragment>
                );
              }
            )}
            <Box marginBottom={'12px'}></Box>
          </Box>
        </>
      ) : type === SpecificationAttributeType.Number ? (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px',
          }}
        >
          <Typography component="span">{label}</Typography>
          <QuantityModifier
            min={0}
            max={20}
            name={name}
            watch={() => _.get(data, name, '') || 0}
            setValue={(name, value) => onChange(value, name)}
            style={{ width: 140 }}
          />
        </Box>
      ) : (
        <FreeInputField
          label={label}
          name={name}
          value={_.get(data, name, '')}
          onChange={onChange}
          type={'text'}
        />
      )}
    </Box>
  );
};

interface ITypedInputFieldProps<T> {
  value: T;
  name: string;
  label: string;
  onChange: (value: string, name: string) => void;
}

const FreeInputField = (
  props: ITypedInputFieldProps<string> & { type: 'number' | 'text' }
) => {
  const [value, setValue] = useState<string>(props.value);
  const debouncedValue: string = useDebounce<string>(value, 800);

  React.useEffect(() => {
    setValue(props.value ?? '');
  }, [props.value]);

  React.useEffect(() => {
    props.onChange(debouncedValue, props.name);
  }, [debouncedValue]);

  return (
    <TextField
      label={props.label}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      variant="outlined"
      fullWidth
      size="small"
      style={{
        marginTop: '.75rem',
      }}
      type={props.type}
    />
  );
};

const DateInputField = (props: ITypedInputFieldProps<Date | null>) => {
  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <KeyboardDateTimePicker
        disableToolbar
        onChange={(date, value) => {
          if (date) props.onChange(formatDateToSQL(date), props.name);
          else props.onChange('', props.name);
        }}
        style={{ marginTop: '.75rem' }}
        format={'dd-MM-yyyy'}
        inputVariant={'outlined'}
        value={props.value}
        KeyboardButtonProps={{
          'aria-label': 'change date',
        }}
        label={props.label}
        placeholder={'Choose a date'}
        size="small"
        views={['year', 'month', 'date']}
        fullWidth
      />
    </MuiPickersUtilsProvider>
  );
};

export default TabOther;
